import { coreLeftView } from '@/components/core-left-view';
import {
  BUTTON_WIDTH,
  GestureDetector,
  SPRING_CONFIG,
  useSwipeLeft
} from '@/components/gesture-handler';
import { Loading } from '@/components/loading';
import { PersonaAvatar } from '@/components/persona-avatar';
import { Text } from '@/components/ui/text';
import { useAuth } from '@/contexts/AuthContext';
import { useAuthedApi } from '@/hooks/use-authed-api';
import { ChevronRight } from '@/icons/ChevronRight';
import { MessageCirclePlus } from '@/icons/MessageCirclePlus';
import { Trash2 } from '@/icons/Trash2';
import { X } from '@/icons/X';
import { generateAPIUrl } from '@/utils/api-url';
import { decryptMessages } from '@/utils/encryption';
import { stripMemoryTags } from '@/utils/memory';
import { HeaderButton } from '@react-navigation/elements';
import { FlashList } from '@shopify/flash-list';
import * as Haptics from 'expo-haptics';
import { Link, Stack, router } from 'expo-router';
import Head from 'expo-router/head';
import { fetch as expoFetch } from 'expo/fetch';
import { hc } from 'hono/client';
import { Fragment, useCallback, useEffect, useState } from 'react';
import {
  ActivityIndicator,
  Dimensions,
  Modal,
  Pressable,
  ScrollView,
  View,
  type ViewStyle
} from 'react-native';
import Animated, {
  interpolate,
  useAnimatedStyle,
  withSpring
} from 'react-native-reanimated';
import { SafeAreaView } from 'react-native-safe-area-context';
import type { ApiChatIdType } from '../../../api/chat/[id]+api';
import type { ApiChatIndexType } from '../../../api/chat/index+api';
import type { ApiChatNewType } from '../../../api/chat/new+api';
import type { ApiPersonaType } from '../../../api/persona/[...everything]+api';

const chatIndexClient = hc<ApiChatIndexType>(generateAPIUrl('/'), {
  fetch: expoFetch as unknown as typeof fetch
});
const chatIdClient = hc<ApiChatIdType>(generateAPIUrl('/'), {
  fetch: expoFetch as unknown as typeof fetch
});
const chatNewClient = hc<ApiChatNewType>(generateAPIUrl('/'), {
  fetch: expoFetch as unknown as typeof fetch
});
const personaClient = hc<ApiPersonaType>(generateAPIUrl('/'), {
  fetch: expoFetch as unknown as typeof fetch
});

type InitialChats = Extract<
  Awaited<ReturnType<typeof getChats>>,
  { chats: any }
>['chats'];
type Chat = InitialChats[number];
type DecryptedLatestMessage = Omit<Chat['latestMessage'], 'contentEnc'> & {
  content: string;
};
type DecryptedChat = Omit<Chat, 'latestMessage'> & {
  latestMessage: DecryptedLatestMessage | null;
};

async function getChats(token: string) {
  const res = await chatIndexClient.api.chat.$get(undefined, {
    headers: { Authorization: `Bearer ${token}` }
  });

  return res.json();
}

async function deleteChat(token: string, id: string) {
  const res = await chatIdClient.api.chat[':id'].$delete(
    {
      param: { id }
    },
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );

  return res.json();
}

async function createChat(token: string, personaId?: string) {
  const res = await chatNewClient.api.chat.new.$post(
    { json: { personaId } },
    { headers: { Authorization: `Bearer ${token}` } }
  );

  if (!res.ok) {
    if (res.status === 429) {
      throw new Error(await res.text());
    }

    throw new Error(
      `Failed to create chat, please try again. ${res.statusText}`
    );
  }

  return res.json();
}

async function loadPersonas(token: string) {
  const res = await personaClient.api.persona.$get(undefined, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return res.json();
}

export default function ChatIndex() {
  const getChatsCallback = useCallback((token: string) => getChats(token), []);
  const { loading, data, authState } = useAuthedApi(getChatsCallback);
  const [personaSelectionModalVisible, setPersonaSelectionModalVisible] =
    useState(false);
  const [isCreatingChat, setIsCreatingChat] = useState(false);

  const personaCount = data?.personaCount ?? 0;
  const maybeNewChat = useCallback(async () => {
    if (!authState.token) {
      alert('Please log in to create a new chat.');
      return;
    }

    if (personaCount === 0) {
      router.navigate('/persona-import');
    } else if (personaCount === 1) {
      setIsCreatingChat(true);
      try {
        const { chatId } = await createChat(authState.token);
        router.navigate(`/chat/${chatId}`);
      } catch (error) {
        console.error('Error creating chat:', error);

        if (error instanceof Error) {
          alert(error.message);
        } else {
          alert('Error creating chat! Please try again later.');
        }
      } finally {
        setIsCreatingChat(false);
      }
    } else {
      setPersonaSelectionModalVisible(true);
    }
  }, [personaCount, authState.token]);

  const MemoizedRightView = useCallback(
    () => <RightView isCreatingChat={isCreatingChat} onPress={maybeNewChat} />,
    [isCreatingChat, maybeNewChat]
  );

  return (
    <Fragment>
      <Head>
        <title>Chats - Lexikon AI</title>
      </Head>

      <Stack.Screen
        options={{
          headerShown: true,
          title: 'Chat',
          headerLargeTitle: true,
          headerStyle: { backgroundColor: '#0f1313' },
          headerLeft: coreLeftView,
          headerRight: () => {
            return <MemoizedRightView />;
          }
        }}
      />

      <SafeAreaView className="flex-1">
        {loading || !authState.decryptionKey ? (
          <Loading />
        ) : (
          <ChatsScreen
            chats={decryptChats(data?.chats || [], authState.decryptionKey)}
          />
        )}

        <View>
          <PersonaSelectionModal
            visible={personaSelectionModalVisible}
            onClose={() => setPersonaSelectionModalVisible(false)}
            onSelect={async (personaId: string) => {
              if (!authState.token) return;
              setIsCreatingChat(true);
              try {
                const { chatId } = await createChat(authState.token, personaId);
                router.navigate(`/chat/${chatId}`);
              } catch (error) {
                console.error('Error creating chat:', error);
              } finally {
                setIsCreatingChat(false);
              }
            }}
          />
        </View>
      </SafeAreaView>
    </Fragment>
  );
}

function decryptChats(chats: Chat[], decryptionKey: string): DecryptedChat[] {
  const messages: NonNullable<Chat['latestMessage']>[] = chats.flatMap(
    (chat) => (chat.latestMessage ? [chat.latestMessage!] : [])
  );

  const decryptedMessages = decryptMessages(messages, decryptionKey);
  const decryptedMessageById = new Map(
    decryptedMessages.map((message) => [message.id, message])
  );

  return chats.map((chat) => {
    const decryptedMessage = chat.latestMessage
      ? decryptedMessageById.get(chat.latestMessage.id) || null
      : null;

    if (decryptedMessage?.content) {
      decryptedMessage.content = stripMemoryTags(decryptedMessage.content);
    }

    return {
      ...chat,
      latestMessage: decryptedMessage
    };
  });
}

function ChatsScreen({ chats }: { chats: DecryptedChat[] }) {
  const [chatsState, setChatsState] = useState(chats);
  const { authState } = useAuth();

  const onDelete = useCallback(
    async (id: string) => {
      if (authState.token) {
        setChatsState((prevChats) =>
          prevChats.filter((chat) => chat.id !== id)
        );
        await deleteChat(authState.token, id);
      }
    },
    [authState.token]
  );

  return (
    <View className="flex-1">
      {chatsState.length === 0 ? (
        <View className="flex-1 items-center justify-center">
          <Text className="text-gray-300">
            No chats yet! Get started by creating a new chat.
          </Text>
        </View>
      ) : null}
      <FlashList
        contentInsetAdjustmentBehavior="automatic"
        data={chatsState}
        renderItem={({ item }) => (
          <MessageRow onDelete={onDelete} chat={item} />
        )}
        estimatedItemSize={72}
        keyExtractor={(item) => item.id}
        keyboardDismissMode="on-drag"
        keyboardShouldPersistTaps="handled"
      />
    </View>
  );
}
function RightView({
  isCreatingChat,
  onPress
}: {
  isCreatingChat: boolean;
  onPress: () => void;
}) {
  return (
    <HeaderButton tintColor="white" onPress={onPress}>
      {isCreatingChat ? (
        <ActivityIndicator size="small" color="white" />
      ) : (
        <MessageCirclePlus size={32} color="white" />
      )}
    </HeaderButton>
  );
}

function MessageRow({
  chat,
  onDelete
}: {
  chat: DecryptedChat;
  onDelete: (id: string) => void;
}) {
  const messageContent =
    chat?.latestMessage?.content
      ?.split('\n')
      ?.filter((m) => m.trim().length > 0)
      ?.reverse()?.[0]
      ?.trim() ?? '';

  return (
    <Swipeable onDelete={() => onDelete(chat.id)}>
      <View className="ml-4 border-b border-gray-500">
        <Link
          href={`/chat/${chat.id}`}
          className="flex flex-row items-center justify-between py-2 pr-3"
        >
          <View className="flex-1 flex-row items-center">
            {chat.persona && (
              <PersonaAvatar persona={chat.persona} className="mr-2" />
            )}
            <View className="flex-1">
              <Text className="native:font-bold text-lg text-gray-300">
                {chat.persona?.name ?? 'Unknown'}
              </Text>
              {messageContent.length ? (
                <Text
                  className="mt-1 text-base font-medium text-gray-400"
                  numberOfLines={1}
                  ellipsizeMode="tail"
                >
                  {messageContent}
                </Text>
              ) : null}
            </View>
            <ChevronRight color="#ccc" size={20} />
          </View>
        </Link>
      </View>
    </Swipeable>
  );
}

const dimensions = Dimensions.get('window');

const ACTION_BUTTON_STYLE: ViewStyle = {
  width: BUTTON_WIDTH
};

function Swipeable({
  children,
  onDelete
}: {
  children: React.ReactNode;
  onDelete: () => void;
}) {
  const { translateX, previousTranslateX, pan } =
    useSwipeLeft(onDeleteWithHaptic);

  const rootStyle = useAnimatedStyle(() => {
    return {
      transform: [{ translateX: translateX.value }]
    };
  });

  const trashActionStyle = useAnimatedStyle(() => {
    return {
      position: 'absolute',
      right: 0,
      flex: 1,
      height: '100%',
      width: interpolate(
        -translateX.value,
        [0, dimensions.width],
        [0, dimensions.width]
      )
    };
  });
  const trashIconStyle = useAnimatedStyle(() => {
    return {
      overflow: 'hidden',
      position: 'absolute',
      right: interpolate(
        -translateX.value,
        [0, BUTTON_WIDTH, dimensions.width],
        [-BUTTON_WIDTH, 0, dimensions.width - BUTTON_WIDTH]
      ),
      flex: 1,
      height: '100%',
      width: BUTTON_WIDTH
    };
  });
  function onDeleteWithHaptic() {
    onDelete();
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
  }

  function onDeleteActionPress() {
    translateX.value = withSpring(-dimensions.width, SPRING_CONFIG);
    onDeleteWithHaptic();
  }

  return (
    <GestureDetector gesture={pan}>
      <View>
        <Animated.View style={trashActionStyle} className="bg-destructive">
          <Animated.View style={trashIconStyle}>
            <Pressable
              style={ACTION_BUTTON_STYLE}
              onPress={onDeleteActionPress}
              className="absolute bottom-0 right-0 top-0 items-center justify-center"
            >
              <Trash2 size={24} color="white" />
            </Pressable>
          </Animated.View>
        </Animated.View>
        <Animated.View style={rootStyle}>{children}</Animated.View>
      </View>
    </GestureDetector>
  );
}

interface PersonaSelectionModalProps {
  visible: boolean;
  onClose: () => void;
  onSelect: (personaId: string) => void;
}

function PersonaSelectionModal({
  visible,
  onClose,
  onSelect
}: PersonaSelectionModalProps) {
  const { authState } = useAuth();
  const [personas, setPersonas] = useState<
    Array<{ id: string; name: string; avatarUrl: string | null }>
  >([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function internalLoadPersonas() {
      if (!authState.token) return;

      try {
        const data = await loadPersonas(authState.token);
        setPersonas(data.personas);
      } catch (error) {
        console.error('Error loading personas:', error);
      } finally {
        setIsLoading(false);
      }
    }

    if (visible) {
      setIsLoading(true);
      internalLoadPersonas();
    }
  }, [visible, authState.token]);

  const handleSelect = (personaId: string) => {
    onSelect(personaId);
    onClose();
  };

  return (
    <Modal animationType="slide" transparent visible={visible}>
      <View className="absolute bottom-0 h-[60%] w-full rounded-t-[18px] bg-[#25292e]">
        <View className="h-20 flex-row items-center justify-between rounded-t-[10px] bg-[#464C55] px-5">
          <View className="w-8" />
          <Text className="text-lg font-medium">Select Persona</Text>
          <Pressable onPress={() => onClose()}>
            <X color="#fff" size={22} />
          </Pressable>
        </View>
        <ScrollView className="mb-6 p-6">
          {isLoading ? (
            <View className="items-center justify-center py-4">
              <ActivityIndicator size="large" color="#fff" />
            </View>
          ) : (
            personas.map((persona) => (
              <Pressable
                key={persona.id}
                onPress={() => handleSelect(persona.id)}
                className="mb-3"
              >
                <View className="flex-row items-center justify-between rounded-md bg-card p-4">
                  <View className="flex-row items-center">
                    <PersonaAvatar persona={persona} className="mr-3" />
                    <Text className="text-lg">{persona.name}</Text>
                  </View>
                  <ChevronRight size={22} className="text-gray-400" />
                </View>
              </Pressable>
            ))
          )}
        </ScrollView>
      </View>
    </Modal>
  );
}
